/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: ActivityActionMultiSelect.tsx
 🗓️ Created: 2025-12-02
 🧩 Description:
    Compact multi-select component for user activity actions (single line)
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import { Activity, ChevronDown } from 'lucide-react';
import { MultiSelectModal } from '../../../components/ui/MultiSelectModal';

interface ActivityActionMultiSelectProps {
  selectedActions: string[];
  onChange: (actions: string[]) => void;
  placeholder?: string;
}

// Available user activity actions
const ACTIVITY_ACTIONS = [
  { id: 'CREATE', name: 'CREATE', description: 'Create operations' },
  { id: 'UPDATE', name: 'UPDATE', description: 'Update operations' },
  { id: 'DELETE', name: 'DELETE', description: 'Delete operations' },
  { id: 'LOGIN', name: 'LOGIN', description: 'User login events' },
  { id: 'LOGOUT', name: 'LOGOUT', description: 'User logout events' },
  { id: 'READ', name: 'READ', description: 'Read/view operations' },
  { id: 'EXECUTE', name: 'EXECUTE', description: 'Execute operations' },
];

export function ActivityActionMultiSelect({
  selectedActions,
  onChange,
  placeholder = 'All Actions',
}: ActivityActionMultiSelectProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get display text
  const getDisplayText = () => {
    if (selectedActions.length === 0) return placeholder;
    if (selectedActions.length === 1) return selectedActions[0];
    return `${selectedActions[0]} +${selectedActions.length - 1}`;
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="flex items-center justify-between w-full h-9 px-3 py-2 text-sm border border-border rounded-md bg-background hover:border-primary/50 focus:border-primary focus:ring-1 focus:ring-primary/20 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Activity className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className={`truncate ${selectedActions.length === 0 ? 'text-muted-foreground' : 'text-foreground'}`}>
            {getDisplayText()}
          </span>
        </div>
        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </button>

      <MultiSelectModal
        title="Select Actions"
        description="Choose the actions you want to filter by"
        items={ACTIVITY_ACTIONS}
        selectedIds={selectedActions}
        onSelectionChange={onChange}
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
        searchPlaceholder="Search actions..."
        icon={Activity}
        enableSelectAll
        selectAllLabel="Select All Actions"
      />
    </>
  );
}

